import Multiselect from 'multiselect-react-dropdown';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Form, Modal, OverlayTrigger, Popover, Table } from 'react-bootstrap';
import { FaAngleDown, FaAngleLeft, FaAngleRight, FaAngleUp, FaEdit, FaTimesCircle, FaTrash } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import BulkEditPriceItemsModal from '../components/BulkEditPriceItemsModal';
import MultiselectWithSelectAll from '../components/MultiselectWithSelectAll';
import PermissionGuard from '../components/PermissionGuard';
import TableSkeleton from '../components/TableSkeleton';
import { getPageCache, setPageCache } from '../utils/pageCache';
import { PERMISSIONS } from '../constants/permissions';
import {
    FORM_SECTION_TITLE_STYLE,
    MULTISELECT_COMMON_PROPS,
    MULTISELECT_THEME_STYLE,
} from '../constants/multiselectTheme';
import './Activities.css';
import { GENERAL_CATEGORY_OPTIONS } from '../constants/categories';
import { apiFetchGLAccounts, apiFetchBillingGroups } from '../utils/billingSetupApi';
import { BILLING_ENABLED } from '../constants/featureFlags';
import {
    getZeroPriceMessage,
    hasLaborMinutes,
    isZeroPrice,
} from '../utils/zeroPriceWarnings';
import { useStaffTypes } from '../hooks/useStaffTypes';
import StaffTypeAbbr from '../components/StaffTypeAbbr';
import '../components/ZeroPriceWarning.css';

const SECTION_TITLE_STYLE = FORM_SECTION_TITLE_STYLE;

// Add minutes formatting function to show 2 decimal places
const formatMinutes = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toFixed(2);
};

// Add currency formatting function
const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return `R ${num.toLocaleString('en-ZA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

function PriceItems() {
    const [AddPriceItem, setAddPriceItem] = useState(false);
    const [priceItems, setPriceItems] = useState([]);
    const [filteredPriceItems, setFilteredPriceItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSites, setSelectedSites] = useState([]);
    const [availableSites, setAvailableSites] = useState([]);
    const [newPriceItemSites, setNewPriceItemSites] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [collapsedRows, setCollapsedRows] = useState({});
    const [customerCareTypes, setCustomerCareTypes] = useState([]);
    const [staffPrices, setStaffPrices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { currentTenant } = useTenant();
    const { getAbbreviation, getDisplayName } = useStaffTypes();
    const { hasPermission } = usePermissions();
    const [searchParams, setSearchParams] = useSearchParams();
    const canViewBilling = BILLING_ENABLED && hasPermission(PERMISSIONS.BILLING_READ);
    const [glAccounts, setGlAccounts] = useState([]);
    const [billingGroupsList, setBillingGroupsList] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [isBulkEditing, setIsBulkEditing] = useState(false);
    const [bulkEditProgress, setBulkEditProgress] = useState({ done: 0, total: 0 });
    const [newPriceItem, setNewPriceItem] = useState({
        item_name: '',
        description: '',
        price: '',
        price_manual: '',
        price_overridden: false,
        customer_care_type_id: '',
        general_category: '',
        gl_allocations: [],
        frequency: '',
        sn_hours_day: '0',
        sn_hours_night: '0',
        ena_hours_day: '0',
        ena_hours_night: '0',
        cw_hours_day: '0',
        cw_hours_night: '0',
        catering: '0',
        cleaning: '0',
        laundry: '0',
        other: '0',
        date_from: '',
        date_to: '',
    });

    useEffect(() => {
        if (currentTenant?.id) {
            try {
                // Stale-while-revalidate: hydrate from this session's cache first,
                // then refresh silently in the background.
                const cached = getPageCache(`priceItems:${currentTenant.id}`);
                if (cached) {
                    setPriceItems(cached.priceItems);
                    setAvailableSites(cached.sites);
                    filterPriceItems(searchTerm, selectedSites, cached.priceItems);
                    setIsLoading(false);
                    fetchPriceItems({ background: true });
                } else {
                    fetchPriceItems();
                }
                fetchCustomerCareTypes();
                fetchStaffPrices();
                if (canViewBilling) {
                    apiFetchGLAccounts(currentTenant.id).then(setGlAccounts).catch(() => setGlAccounts([]));
                    apiFetchBillingGroups(currentTenant.id).then(setBillingGroupsList).catch(() => setBillingGroupsList([]));
                } else {
                    setGlAccounts([]);
                    setBillingGroupsList([]);
                }
            } catch (error) {
                console.error('Error in useEffect:', error);
            }
        } else {
            setPriceItems([]);
            setFilteredPriceItems([]);
            setIsLoading(false);
        }
    }, [currentTenant?.id, canViewBilling]);

    const fetchCustomerCareTypes = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/customerCareTypes`);
            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data)) {
                    const updatedCareTypes = data.map(element => ({
                        name: element.Name || element.name,
                        id: element.CareTypeID || element.care_type_id
                    }));
                    setCustomerCareTypes(updatedCareTypes);
                } else {
                    console.error("Error: Expected an array but received", data);
                }
            } else {
                console.error('Failed to fetch customer care types');
            }
        } catch (error) {
            console.error('An error occurred while fetching customer care types:', error);
        }
    };

    const fetchSites = async () => {
        if (!currentTenant) {
            setAvailableSites([]);
            return;
        }

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/sites?tenant_id=${currentTenant.id}`);
            if (response.ok) {
                const data = await response.json();
                // Ensure data is an array before mapping
                const sitesArray = Array.isArray(data) ? data : [];
                // Map the backend response to match the expected format
                const mappedSites = sitesArray.map(site => ({
                    site_id: site.id || site.site_id,
                    site_name: site.name || site.site_name
                }));
                setAvailableSites(mappedSites);
            } else {
                console.error('Failed to fetch sites');
            }
        } catch (error) {
            console.error('Error fetching sites:', error);
        }
    };

    // Fetch staff prices
    const fetchStaffPrices = async () => {
        if (!currentTenant?.id) {
            setStaffPrices([]);
            return;
        }
        try {
            const response = await fetchWithAuth(
                `${API_BASE_URL}/staff/prices?tenant_id=${encodeURIComponent(currentTenant.id)}`
            );
            if (response.ok) {
                const data = await response.json();
                // Transform snake_case field names from API to PascalCase for frontend compatibility
                const transformedData = (Array.isArray(data) ? data : []).map(item => ({
                    StaffID: item.id || item.flow_staff_id,
                    StaffType: item.staff_type_code || item.flow_staff_type,
                    Year: String(item.year),
                    StandardHourlyPrice: item.standard_hourly_price
                }));
                setStaffPrices(transformedData);
            } else {
                console.error('Failed to fetch staff prices');
            }
        } catch (error) {
            console.error('An error occurred while fetching staff prices:', error);
        }
    };

    // Calculate price based on the formula: Price = Additional costs + (labour x Rate)
    const calculatePrice = (item) => {
        // If manual price is set and override is active, use it (allow intentional 0)
        if (item.price_overridden && item.price_manual != null && item.price_manual !== '') {
            return item.price_manual;
        }

        // Calculate additional costs
        const catering = parseFloat(item.catering || item.Catering || 0);
        const cleaning = parseFloat(item.cleaning || item.Cleaning || 0);
        const laundry = parseFloat(item.laundry || item.Laundry || 0);
        const other = parseFloat(item.other || item.Other || 0);
        const additionalCosts = catering + cleaning + laundry + other;

        // Get all available staff prices (fallback to any year if current year not found)
        let currentStaffPrices = [];
        if (staffPrices.length > 0) {
            const currentYear = new Date().getFullYear().toString();
            currentStaffPrices = staffPrices.filter(price => price.Year === currentYear);

            // If no prices for current year, use first available year
            if (currentStaffPrices.length === 0) {
                const firstYear = staffPrices[0].Year;
                currentStaffPrices = staffPrices.filter(price => price.Year === firstYear);
            }
        }

        // Get hourly rates for each staff type
        const snRate = currentStaffPrices.find(price => price.StaffType === 'SN')?.StandardHourlyPrice || 0;
        const enaRate = currentStaffPrices.find(price => price.StaffType === 'ENA')?.StandardHourlyPrice || 0;
        const cwRate = currentStaffPrices.find(price => price.StaffType === 'CW')?.StandardHourlyPrice || 0;

        // Convert minutes to hours for each staff type
        const snHoursDay = parseFloat(item.sn_hours_day || 0) / 60;
        const snHoursNight = parseFloat(item.sn_hours_night || 0) / 60;
        const enaHoursDay = parseFloat(item.ena_hours_day || 0) / 60;
        const enaHoursNight = parseFloat(item.ena_hours_night || 0) / 60;
        const cwHoursDay = parseFloat(item.cw_hours_day || 0) / 60;
        const cwHoursNight = parseFloat(item.cw_hours_night || 0) / 60;

        // Calculate total labour cost
        const snLabour = (snHoursDay + snHoursNight) * snRate;
        const enaLabour = (enaHoursDay + enaHoursNight) * enaRate;
        const cwLabour = (cwHoursDay + cwHoursNight) * cwRate;
        const totalLabour = snLabour + enaLabour + cwLabour;

        // Calculate total price
        const totalPrice = additionalCosts + totalLabour;

        return totalPrice.toFixed(2);
    };

    // Reset price to calculated value
    const resetPriceToCalculated = (item, setItem) => {
        // Force formula calc — calculatePrice short-circuits while price_overridden is still true
        const calculatedPrice = calculatePrice({ ...item, price_overridden: false });
        setItem(prev => ({
            ...prev,
            price: calculatedPrice,
            price_manual: '',
            price_overridden: false
        }));
    };

    // Recalculate prices whenever staff prices are fetched or updated
    useEffect(() => {
        if (staffPrices.length > 0) {
            // Recalculate prices for all existing items
            setPriceItems(prevItems =>
                prevItems.map(item => ({
                    ...item,
                    price: calculatePrice(item)
                }))
            );
            // Also update the edit modal's displayed price if it isn't manually overridden
            setFormData(prev => {
                if (!prev.price_overridden) {
                    return { ...prev, price: calculatePrice(prev) };
                }
                return prev;
            });
        }
    }, [staffPrices]);

    const handleSort = (key, isDate = false) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }

        const sortedData = [...filteredPriceItems].sort((a, b) => {
            if (isDate) {
                return direction === 'ascending'
                    ? new Date(a[key]) - new Date(b[key])
                    : new Date(b[key]) - new Date(a[key]);
            } else {
                return direction === 'ascending'
                    ? (a[key] || '').toString().localeCompare((b[key] || '').toString())
                    : (b[key] || '').toString().localeCompare((a[key] || '').toString());
            }
        });

        setFilteredPriceItems(sortedData);
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? <FaAngleUp /> : <FaAngleDown />;
        }
        return null;
    };

    const fetchPriceItems = async ({ background = false } = {}) => {
        if (!currentTenant) {
            setIsLoading(false);
            return;
        }

        const tenantId = currentTenant.id;

        if (!background) setIsLoading(true);

        try {
            // Step 1: Fetch all sites for this tenant first
            const sitesResponse = await fetchWithAuth(`${API_BASE_URL}/sites?tenant_id=${tenantId}`);

            let tenantSites = [];
            if (sitesResponse.ok) {
                const sitesData = await sitesResponse.json();
                const sitesArray = Array.isArray(sitesData) ? sitesData : [];
                
                // Use id which is the site UUID
                tenantSites = sitesArray.map(site => ({
                    site_id: site.id || site.site_id,
                    site_name: site.name || ''
                }));
            }

            // Reuse the sites we just fetched for the filter dropdown, so we don't
            // fire a second /sites request on load.
            setAvailableSites(tenantSites);
            // Step 2: Fetch price items from tenant-scoped API for each site
            const priceItemMap = new Map(); // price_list_item_key -> price item with sites array
            
            if (tenantSites.length > 0) {
                // Fetch price items for each site
                const priceItemPromises = tenantSites.map(async (ts) => {
                    try {
                        const response = await fetchWithAuth(
                            `${API_BASE_URL}/tenant-site-price-list-items?tenant_id=${tenantId}&site_id=${ts.site_id}`
                        );
                        
                        if (response.ok) {
                            const data = await response.json();
                            const itemsArray = Array.isArray(data) ? data : [];
                            return { tenantSite: ts, priceItems: itemsArray };
                        } else {
                            const errorText = await response.text();
                            console.error(`Failed to fetch price items for site ${ts.site_id}: ${response.status}`, errorText);
                            return { tenantSite: ts, priceItems: [] };
                        }
                    } catch (err) {
                        console.error(`Error fetching price items for site ${ts.site_id}:`, err);
                        return { tenantSite: ts, priceItems: [] };
                    }
                });

                const results = await Promise.all(priceItemPromises);
                
                // Combine all price items - group by price_list_item_key and collect all sites
                results.forEach(({ tenantSite, priceItems }) => {
                    if (!Array.isArray(priceItems)) {
                        return;
                    }
                    priceItems.forEach(item => {
                        const priceItemKey = item.price_list_item_key;
                        const meta = item.metadata || {};
                        
                        if (priceItemKey && priceItemMap.has(priceItemKey)) {
                            // Price item already exists, add this site to its sites array
                            const existing = priceItemMap.get(priceItemKey);
                            if (existing && existing.sites) {
                                const siteExists = existing.sites.some(s => s.site_id === tenantSite.site_id);
                                if (!siteExists) {
                                    existing.sites.push({
                                        site_id: tenantSite.site_id,
                                        site_name: tenantSite.site_name,
                                        record_id: item.id,
                                        tenant_site_id: item.tenant_site_id
                                    });
                                }
                            }
                        } else if (priceItemKey) {
                            // New price item - create entry
                            const meta = item.metadata || {};
                            
                            const extractedData = {
                                // Extract from metadata - minutes are stored in catalogue.defaults
                                item_name: meta.item_name || item.price_list_item_name || '',
                                description: meta.description || '',
                                price: meta.price != null && meta.price !== '' ? meta.price : calculatePrice(meta),
                                price_manual: meta.price_manual != null && meta.price_manual !== '' ? meta.price_manual : '',
                                price_overridden: meta.price_overridden || false,
                                // Read both default_on_quote and customer_care_type_id for backward compatibility
                                customer_care_type_id: meta.default_on_quote || meta.customer_care_type_id || '',
                                general_category: meta.catalogue?.general_category || meta.general_category || '',
                                gl_allocations: meta.gl_allocations || (meta.gl_group ? [{ gl_account_code: meta.gl_group, amount: parseFloat(meta.price) || 0 }] : []),
                                frequency: meta.catalogue?.defaults?.frequency || meta.frequency?.toString() || '',
                                sn_hours_day: meta.catalogue?.defaults?.sn_hours_day?.toString() || meta.sn_hours_day?.toString() || '0',
                                sn_hours_night: meta.catalogue?.defaults?.sn_hours_night?.toString() || meta.sn_hours_night?.toString() || '0',
                                ena_hours_day: meta.catalogue?.defaults?.ena_hours_day?.toString() || meta.ena_hours_day?.toString() || '0',
                                ena_hours_night: meta.catalogue?.defaults?.ena_hours_night?.toString() || meta.ena_hours_night?.toString() || '0',
                                cw_hours_day: meta.catalogue?.defaults?.cw_hours_day?.toString() || meta.cw_hours_day?.toString() || '0',
                                cw_hours_night: meta.catalogue?.defaults?.cw_hours_night?.toString() || meta.cw_hours_night?.toString() || '0',
                                // Additional costs (extras)
                                catering: meta.catering?.toString() || '0',
                                cleaning: meta.cleaning?.toString() || '0',
                                laundry: meta.laundry?.toString() || '0',
                                other: meta.other?.toString() || '0',
                                // Date range for validity
                                date_from: meta.date_from || '',
                                date_to: meta.date_to || '',
                            };
                            
                            priceItemMap.set(priceItemKey, {
                                id: item.id,
                                price_list_item_id: item.id,
                                price_list_item_key: item.price_list_item_key,
                                price_list_item_name: item.price_list_item_name,
                                tenant_id: item.tenant_id,
                                tenant_site_id: item.tenant_site_id,
                                metadata: meta,
                                created_at: item.created_at,
                                updated_at: item.updated_at,
                                ...extractedData,
                                // Include site info
                                sites: [{
                                    site_id: tenantSite.site_id,
                                    site_name: tenantSite.site_name,
                                    record_id: item.id,
                                    tenant_site_id: item.tenant_site_id
                                }]
                            });
                        }
                    });
                });
            }

            // Convert map to array
            const uniquePriceItems = Array.from(priceItemMap.values());

            const sorted = [...uniquePriceItems].sort((a, b) =>
                (a.item_name || '').localeCompare(b.item_name || '')
            );

            setPriceItems(sorted);
            setPageCache(`priceItems:${tenantId}`, { priceItems: sorted, sites: tenantSites });
            // Re-apply current search/site filters instead of resetting to the full list
            filterPriceItems(searchTerm, selectedSites, sorted);
            if (!sortConfig.key) {
                setSortConfig({ key: 'item_name', direction: 'ascending' });
            }
        } catch (error) {
            console.error('Error fetching price items:', error);
            setPriceItems([]);
            setFilteredPriceItems([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Get sites for the filter dropdown
    // Returns availableSites (all tenant sites) to allow filtering by any tenant site
    const getUniqueSites = () => {
        // Return availableSites which contains all tenant sites
        return availableSites;
    };

    const handleSearchChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        filterPriceItems(value, selectedSites);
    };

    const handleSiteFilterChange = (selectedList) => {
        setSelectedSites(selectedList);
        filterPriceItems(searchTerm, selectedList);
    };

    const filterPriceItems = (searchValue, siteFilters, baseItems = null) => {
        let filtered = baseItems !== null ? baseItems : priceItems;

        // Filter by search term (item name or item key)
        if (searchValue) {
            filtered = filtered.filter(item =>
                item.item_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                item.price_list_item_key?.toLowerCase().includes(searchValue.toLowerCase())
            );
        }

        // Filter by selected sites - check against the sites array
        if (siteFilters && siteFilters.length > 0) {
            const selectedSiteIds = siteFilters.map(site => site.site_id);
            filtered = filtered.filter(item =>
                item.sites && item.sites.some(site => selectedSiteIds.includes(site.site_id))
            );
        }

        setFilteredPriceItems(filtered);
    };

    // Component to display sites as compact badges
    const SitesDisplay = ({ sites }) => {
        if (!sites || sites.length === 0) {
            return <span className="text-muted">-</span>;
        }

        const maxVisible = 2;
        const visibleSites = sites.slice(0, maxVisible);
        const remainingCount = sites.length - maxVisible;
        const tooltipId = `sites-tooltip-${Math.random().toString(36).substr(2, 9)}`;

        return (
            <div className="sites-container">
                {visibleSites.map((site, index) => (
                    <span key={site.site_id} className="site-badge">
                        {site.site_name}
                    </span>
                ))}
                {remainingCount > 0 && (
                    <>
                        <span
                            className="site-badge site-badge-more"
                            data-tooltip-id={tooltipId}
                        >
                            +{remainingCount}
                        </span>
                        <ReactTooltip
                            id={tooltipId}
                            place="top"
                            content={
                                <div style={{ textAlign: 'left' }}>
                                    {sites.slice(maxVisible).map(site => (
                                        <div key={site.site_id}>{site.site_name}</div>
                                    ))}
                                </div>
                            }
                        />
                    </>
                )}
            </div>
        );
    };

    const DescriptionCell = ({ description, id }) => {
        const textRef = useRef(null);
        const [isTruncated, setIsTruncated] = useState(false);

        useLayoutEffect(() => {
            const el = textRef.current;
            if (!el) {
                setIsTruncated(false);
                return undefined;
            }

            const checkOverflow = () => {
                setIsTruncated(el.scrollWidth > el.clientWidth + 1);
            };

            checkOverflow();
            const observer = typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(checkOverflow)
                : null;
            observer?.observe(el);
            window.addEventListener('resize', checkOverflow);
            return () => {
                observer?.disconnect();
                window.removeEventListener('resize', checkOverflow);
            };
        }, [description]);

        if (!description) {
            return null;
        }

        const popover = (
            <Popover id={`description-popover-${id || 'item'}`} className="description-popover">
                <Popover.Header as="div" className="description-popover-header">Description</Popover.Header>
                <Popover.Body className="description-popover-body">
                    {description}
                </Popover.Body>
            </Popover>
        );

        return (
            <div className="description-cell">
                <span
                    ref={textRef}
                    className="description-cell-text"
                    title={!isTruncated ? description : undefined}
                >
                    {description}
                </span>
                {isTruncated && (
                    <OverlayTrigger trigger="click" placement="auto" overlay={popover} rootClose>
                        <button
                            type="button"
                            className="description-more-btn"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="View full description"
                        >
                            more
                        </button>
                    </OverlayTrigger>
                )}
            </div>
        );
    };

    const handleAddPriceItemClick = () => {
        setAddPriceItem(true);
        fetchSites();
    };

    const toggleRowCollapse = (itemId) => {
        setCollapsedRows(prevState => ({
            ...prevState,
            [itemId]: !prevState[itemId],
        }));
    };

    // Add validation function for required fields
    const validateRequiredFields = (data, sites) => {
        const errors = {};
        
        // Validate item_name as required
        if (!data.item_name || data.item_name.toString().trim() === '') {
            errors.item_name = 'Price item name is required';
        }

        // Validate that at least one site is selected
        if (!sites || sites.length === 0) {
            errors.sites = 'Please select at least one site';
        }

        return errors;
    };

    const handleAddPriceItem = async () => {
        // Auto-generate price_list_item_key in format "priceitem_XXX" where XXX is next available number
        // Find the highest price item number from existing items
        let maxPriceItemNumber = 0;
        priceItems.forEach(item => {
            const match = item.price_list_item_key?.match(/priceitem_(\d+)/);
            if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxPriceItemNumber) {
                    maxPriceItemNumber = num;
                }
            }
        });
        
        // Next price item number
        const nextPriceItemNumber = maxPriceItemNumber + 1;
        const generatedPriceItemKey = `priceitem_${nextPriceItemNumber}`;

        // Check for duplicate price item key within the same tenant
        const priceItemKeyExists = priceItems.some(item =>
            item.price_list_item_key?.toLowerCase() === generatedPriceItemKey?.toLowerCase()
        );

        if (priceItemKeyExists) {
            alert('A price item with this key already exists. Please try again.');
            return;
        }

        // Validate all required fields
        const errors = validateRequiredFields(newPriceItem, newPriceItemSites);
        setValidationErrors(errors);

        if (Object.keys(errors).length > 0) {
            const missingFields = Object.keys(errors).map(field => {
                // Convert field names to user-friendly labels
                const fieldLabels = {
                    'item_name': 'Price Item Name',
                    'sites': 'Sites'
                };
                return fieldLabels[field] || field;
            });

            alert(`Please complete the following required fields:\n\n• ${missingFields.join('\n• ')}`);
            return; // Stop execution
        }

        // Use manual price if overridden, otherwise calculate
        const priceToSave = newPriceItem.price_overridden ? newPriceItem.price_manual : calculatePrice(newPriceItem);

        if (canViewBilling) {
            // Validate all GL allocation rows have an account selected
            if (newPriceItem.gl_allocations.some(a => !a.gl_account_code)) {
                setValidationErrors(prev => ({ ...prev, gl_allocations: true }));
                return;
            }

            // Validate GL allocations don't exceed price
            const allocTotal = newPriceItem.gl_allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
            if (allocTotal > parseFloat(priceToSave) + 0.005) {
                alert(`GL allocations (R ${allocTotal.toFixed(2)}) exceed the item price (R ${parseFloat(priceToSave).toFixed(2)}). Please reduce allocations before saving.`);
                return;
            }
        }

        try {
            // NEW API: Create one price item record per selected site
            // This ensures each price item is linked to a specific tenant_site_id
            const careTypeId = newPriceItem.customer_care_type_id?.trim();
            
            // Store metadata in nested format matching backend structure
            // Also include flat fields at root level for easier querying
            const metadata = {
                item: {
                    key: newPriceItem.item_name?.trim().toLowerCase().replace(/\s+/g, '_'),
                    name: newPriceItem.item_name || ''
                },
                catalogue: {
                    general_category: newPriceItem.general_category || '',
                    defaults: {
                        frequency: newPriceItem.frequency || '',
                        sn_hours_day: newPriceItem.sn_hours_day || '0',
                        sn_hours_night: newPriceItem.sn_hours_night || '0',
                        ena_hours_day: newPriceItem.ena_hours_day || '0',
                        ena_hours_night: newPriceItem.ena_hours_night || '0',
                        cw_hours_day: newPriceItem.cw_hours_day || '0',
                        cw_hours_night: newPriceItem.cw_hours_night || '0'
                    }
                },
                // Default on quote - which care type this item defaults to on quotes
                default_on_quote: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                description: newPriceItem.description || '',
                gl_allocations: newPriceItem.gl_allocations || [],
                // Additional costs (extras)
                catering: newPriceItem.catering || '0',
                cleaning: newPriceItem.cleaning || '0',
                laundry: newPriceItem.laundry || '0',
                other: newPriceItem.other || '0',
                price: priceToSave,
                price_overridden: newPriceItem.price_overridden || false,
                price_manual: newPriceItem.price_manual || '',
                // Date range for validity
                date_from: newPriceItem.date_from || '',
                date_to: newPriceItem.date_to || ''
            };

            // Create one price item per selected site
            const creationPromises = newPriceItemSites.map(async (site) => {
                // IMPORTANT: Send the fields at the TOP LEVEL of the request body, not just in metadata
                // The backend's POST handler extracts top-level fields and builds metadata from them
                const formattedPriceItem = {
                    tenant_id: currentTenant?.id,
                    site_id: site.site_id, // Required - the backend resolves to tenant_site_id
                    price_list_item_key: generatedPriceItemKey,
                    price_list_item_name: newPriceItem.item_name || '',
                    // Send individual fields at top level (backend extracts these)
                    item_name: newPriceItem.item_name || '',
                    price: priceToSave,
                    customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                    general_category: newPriceItem.general_category || '',
                    gl_allocations: newPriceItem.gl_allocations || [],
                    frequency: newPriceItem.frequency || '',
                    sn_hours_day: newPriceItem.sn_hours_day || '0',
                    sn_hours_night: newPriceItem.sn_hours_night || '0',
                    ena_hours_day: newPriceItem.ena_hours_day || '0',
                    ena_hours_night: newPriceItem.ena_hours_night || '0',
                    cw_hours_day: newPriceItem.cw_hours_day || '0',
                    cw_hours_night: newPriceItem.cw_hours_night || '0',
                    // Additional costs (extras) - send at top level
                    catering: newPriceItem.catering || '0',
                    cleaning: newPriceItem.cleaning || '0',
                    laundry: newPriceItem.laundry || '0',
                    other: newPriceItem.other || '0',
                    // Date range for validity - send at top level
                    date_from: newPriceItem.date_from || '',
                    date_to: newPriceItem.date_to || '',
                    description: newPriceItem.description || '',
                    // Also include in metadata for backward compatibility
                    metadata: metadata
                };

                const response = await fetchWithAuth(`${API_BASE_URL}/tenant-site-price-list-items`, {
                    method: 'POST',
                    body: JSON.stringify(formattedPriceItem),
                });

                return response;
            });

            const results = await Promise.all(creationPromises);
            const allSuccessful = results.every(res => res.ok);

            if (!allSuccessful) {
                const errorTexts = await Promise.all(results.map(res => res.text()));
                console.error('Error responses:', errorTexts);
                alert('Some price items could not be created. Please try again.');
                return;
            }

            fetchPriceItems();
            setAddPriceItem(false);
            setNewPriceItem({
                item_name: '',
                description: '',
                price: '',
                customer_care_type_id: '',
                general_category: '',
                gl_allocations: [],
                frequency: '',
                sn_hours_day: '0',
                sn_hours_night: '0',
                ena_hours_day: '0',
                ena_hours_night: '0',
                cw_hours_day: '0',
                cw_hours_night: '0',
                catering: '0',
                cleaning: '0',
                laundry: '0',
                other: '0',
                date_from: '',
                date_to: '',
            });
            setNewPriceItemSites([]);
            setValidationErrors({}); // Clear validation errors
        } catch (error) {
            console.error('Error adding price item:', error);
            alert('An error occurred while creating the price item. Please try again.');
        }
    };

    function debounce(fn, delay) {
        let timeoutID;
        return (...args) => {
            if (timeoutID) {
                clearTimeout(timeoutID);
            }
            timeoutID = setTimeout(() => {
                fn(...args);
            }, delay);
        };
    }

    const handleEditPriceItemDebounced = debounce(async (priceItemKey, updatedItem, fetchPriceItems) => {
        try {
            // Handle customer_care_type_id - keep as comma-separated string for multiple values
            const careTypeId = updatedItem.customer_care_type_id?.trim();
            
            // Use manual price if overridden, otherwise calculate
            const priceToSave = updatedItem.price_overridden ? updatedItem.price_manual : calculatePrice(updatedItem);
            
            // Build metadata object in nested format for the new API
            const metadata = {
                item: {
                    key: updatedItem.item_name || '',
                    name: updatedItem.item_name
                },
                catalogue: {
                    general_category: updatedItem.general_category || '',
                    defaults: {
                        frequency: updatedItem.frequency || '',
                        sn_hours_day: updatedItem.sn_hours_day || '0',
                        sn_hours_night: updatedItem.sn_hours_night || '0',
                        ena_hours_day: updatedItem.ena_hours_day || '0',
                        ena_hours_night: updatedItem.ena_hours_night || '0',
                        cw_hours_day: updatedItem.cw_hours_day || '0',
                        cw_hours_night: updatedItem.cw_hours_night || '0'
                    }
                },
                customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                description: updatedItem.description || '',
                gl_allocations: updatedItem.gl_allocations || [],
                catering: updatedItem.catering || '0',
                cleaning: updatedItem.cleaning || '0',
                laundry: updatedItem.laundry || '0',
                other: updatedItem.other || '0',
                price: priceToSave,
                price_overridden: updatedItem.price_overridden || false,
                price_manual: updatedItem.price_manual || '',
                date_from: updatedItem.date_from || '',
                date_to: updatedItem.date_to || ''
            };

            // Send fields at top level (backend extracts these for metadata)
            const formattedPriceItem = {
                price_list_item_name: updatedItem.item_name || '',
                // Send individual fields at top level (backend extracts these)
                item_name: updatedItem.item_name || '',
                price: priceToSave,
                customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                general_category: updatedItem.general_category || '',
                gl_allocations: updatedItem.gl_allocations || [],
                frequency: updatedItem.frequency || '',
                sn_hours_day: updatedItem.sn_hours_day || '0',
                sn_hours_night: updatedItem.sn_hours_night || '0',
                ena_hours_day: updatedItem.ena_hours_day || '0',
                ena_hours_night: updatedItem.ena_hours_night || '0',
                cw_hours_day: updatedItem.cw_hours_day || '0',
                cw_hours_night: updatedItem.cw_hours_night || '0',
                // Additional costs (extras)
                catering: updatedItem.catering || '0',
                cleaning: updatedItem.cleaning || '0',
                laundry: updatedItem.laundry || '0',
                other: updatedItem.other || '0',
                // Date range
                date_from: updatedItem.date_from || '',
                date_to: updatedItem.date_to || '',
                description: updatedItem.description || '',
                // Also include metadata for backward compatibility
                metadata: metadata
            };

            // Get site_id from the item's sites array
            const siteId = updatedItem.sites?.[0]?.site_id;

            // Use new tenant-scoped API endpoint - requires both tenant_id AND site_id
            const url = siteId
                ? `${API_BASE_URL}/tenant-site-price-list-items/${priceItemKey}?tenant_id=${currentTenant?.id}&site_id=${siteId}`
                : `${API_BASE_URL}/tenant-site-price-list-items/${priceItemKey}?tenant_id=${currentTenant?.id}`;
            
            const response = await fetchWithAuth(url, {
                method: 'PUT',
                body: JSON.stringify(formattedPriceItem),
            });

            if (response.ok) {
                fetchPriceItems();
            } else {
                console.error('Failed to update price item');
            }
        } catch (error) {
            console.error('Error updating price item:', error);
        }
    }, 50);

    const [editPriceItemId, setEditPriceItemId] = useState(null);
    const [editPriceItemKey, setEditPriceItemKey] = useState(null);
    const [formData, setFormData] = useState({
        item_name: '',
        description: '',
        price: '',
        price_manual: '',
        price_overridden: false,
        customer_care_type_id: '',
        general_category: '',
        gl_allocations: [],
        frequency: '',
        sn_hours_day: '',
        sn_hours_night: '',
        ena_hours_day: '',
        ena_hours_night: '',
        cw_hours_day: '',
        cw_hours_night: '',
        catering: '',
        cleaning: '',
        laundry: '',
        other: '',
        date_from: '',
        date_to: '',
    });
    const [editPriceItemSites, setEditPriceItemSites] = useState([]);

    const handleEditClick = (item) => {
        // Use price_list_item_key for new API
        setEditPriceItemId(item.id || item.price_list_item_id);
        setEditPriceItemKey(item.price_list_item_key || '');
        
        // Check if price was manually overridden (stored in metadata)
        const isOverridden = item.metadata?.price_overridden || false;
        const manualPrice = item.metadata?.price_manual ?? item.price ?? '';
        
        const loadedFormData = {
            item_name: item.item_name || '',
            description: item.description || '',
            price: item.price ?? '',
            price_manual: manualPrice,
            price_overridden: isOverridden,
            customer_care_type_id: item.customer_care_type_id || '',
            general_category: item.general_category || '',
            gl_allocations: item.gl_allocations || [],
            frequency: item.frequency || '',
            sn_hours_day: item.sn_hours_day || '',
            sn_hours_night: item.sn_hours_night || '',
            ena_hours_day: item.ena_hours_day || '',
            ena_hours_night: item.ena_hours_night || '',
            cw_hours_day: item.cw_hours_day || '',
            cw_hours_night: item.cw_hours_night || '',
            catering: item.catering || '',
            cleaning: item.cleaning || '',
            laundry: item.laundry || '',
            other: item.other || '',
            date_from: item.date_from || '',
            date_to: item.date_to || ''
        };

        // Compute the displayed price once so it stays stable during re-renders
        // (e.g. adding a site triggers a re-render but must not clear the price)
        if (!isOverridden) {
            loadedFormData.price = calculatePrice(loadedFormData);
        } else {
            loadedFormData.price = manualPrice;
        }

        setFormData(loadedFormData);
        
        // Populate editPriceItemSites with ALL sites from the item's sites array
        const sites = item.sites || [];
        setEditPriceItemSites(sites);
        // Fetch sites if not already loaded
        if (availableSites.length === 0) {
            fetchSites();
        }
    };

    // Deep-link from billing unallocated panel: /priceList?edit=<price_list_item_key>
    useEffect(() => {
        const editKey = searchParams.get('edit');
        if (!editKey || isLoading || editPriceItemKey) return;
        const item = priceItems.find(i => i.price_list_item_key === editKey);
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        if (item) {
            handleEditClick(item);
            setSearchParams(next, { replace: true });
            return;
        }
        if (priceItems.length > 0) {
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- open once when list is ready
    }, [searchParams, priceItems, isLoading, editPriceItemKey]);

    const handleSaveClick = async () => {
        // Validate that at least one site is selected
        if (!editPriceItemSites || editPriceItemSites.length === 0) {
            alert('Please select at least one site');
            return;
        }

        try {
            // Use manual price if overridden, otherwise calculate
            const priceToSave = formData.price_overridden ? formData.price_manual : calculatePrice(formData);

            if (canViewBilling) {
                // Validate all GL allocation rows have an account selected
                if (formData.gl_allocations.some(a => !a.gl_account_code)) {
                    setValidationErrors(prev => ({ ...prev, gl_allocations: true }));
                    return;
                }

                // Validate GL allocations don't exceed price
                const editAllocTotal = formData.gl_allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                if (editAllocTotal > parseFloat(priceToSave) + 0.005) {
                    alert(`GL allocations (R ${editAllocTotal.toFixed(2)}) exceed the item price (R ${parseFloat(priceToSave).toFixed(2)}). Please reduce allocations before saving.`);
                    return;
                }
            }
            // Convert PascalCase to snake_case for Supabase
            // Handle customer_care_type_id - keep as comma-separated string for multiple values
            const careTypeId = formData.customer_care_type_id?.trim();
            
            const metadata = {
                item: {
                    key: editPriceItemKey, // Use the stored key
                    name: formData.item_name
                },
                catalogue: {
                    general_category: formData.general_category || '',
                    defaults: {
                        frequency: formData.frequency || '',
                        sn_hours_day: formData.sn_hours_day || '0',
                        sn_hours_night: formData.sn_hours_night || '0',
                        ena_hours_day: formData.ena_hours_day || '0',
                        ena_hours_night: formData.ena_hours_night || '0',
                        cw_hours_day: formData.cw_hours_day || '0',
                        cw_hours_night: formData.cw_hours_night || '0'
                    }
                },
                // Default on quote - which care type this item defaults to on quotes
                default_on_quote: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                description: formData.description || '',
                gl_allocations: formData.gl_allocations || [],
                // Additional costs (extras)
                catering: formData.catering || '0',
                cleaning: formData.cleaning || '0',
                laundry: formData.laundry || '0',
                other: formData.other || '0',
                price: priceToSave,
                price_overridden: formData.price_overridden || false,
                price_manual: formData.price_manual || '',
                // Date range for validity
                date_from: formData.date_from || '',
                date_to: formData.date_to || ''
            };

            // Send fields at top level (backend extracts these for metadata)
            const formattedPriceItem = {
                price_list_item_name: formData.item_name || '',
                // Send individual fields at top level (backend extracts these)
                item_name: formData.item_name || '',
                price: priceToSave,
                customer_care_type_id: careTypeId && careTypeId.length > 0 ? careTypeId : null,
                general_category: formData.general_category || '',
                gl_allocations: formData.gl_allocations || [],
                frequency: formData.frequency || '',
                sn_hours_day: formData.sn_hours_day || '0',
                sn_hours_night: formData.sn_hours_night || '0',
                ena_hours_day: formData.ena_hours_day || '0',
                ena_hours_night: formData.ena_hours_night || '0',
                cw_hours_day: formData.cw_hours_day || '0',
                cw_hours_night: formData.cw_hours_night || '0',
                // Additional costs (extras)
                catering: formData.catering || '0',
                cleaning: formData.cleaning || '0',
                laundry: formData.laundry || '0',
                other: formData.other || '0',
                // Date range
                date_from: formData.date_from || '',
                date_to: formData.date_to || '',
                description: formData.description || '',
                // Also include metadata for backward compatibility
                metadata: metadata
            };

            // Get original sites from the item being edited
            const originalItem = priceItems.find(item => item.price_list_item_key === editPriceItemKey);
            const originalSites = originalItem?.sites || [];
            const originalSiteIds = new Set(originalSites.map(s => s.site_id));
            
            // Get the new site IDs selected by user
            const newSiteIds = new Set(editPriceItemSites.map(s => s.site_id));
            
            // Separate into:
            // - sites to update (exist in both original and new)
            // - sites to create (new, not in original)
            // - sites to delete (in original but not in new - user deselected them)
            const sitesToUpdate = [];
            const sitesToCreate = [];
            const sitesToDelete = [];
            
            editPriceItemSites.forEach(site => {
                if (originalSiteIds.has(site.site_id)) {
                    sitesToUpdate.push(site);
                } else {
                    sitesToCreate.push(site);
                }
            });
            
            // Find sites that were removed (deselected in the modal)
            originalSites.forEach(site => {
                if (!newSiteIds.has(site.site_id)) {
                    sitesToDelete.push(site);
                }
            });

            const promises = [];

            // UPDATE existing sites using PUT
            sitesToUpdate.forEach(site => {
                const editSiteId = site?.site_id;
                const url = editSiteId
                    ? `${API_BASE_URL}/tenant-site-price-list-items/${editPriceItemKey}?tenant_id=${currentTenant?.id}&site_id=${editSiteId}`
                    : `${API_BASE_URL}/tenant-site-price-list-items/${editPriceItemKey}?tenant_id=${currentTenant?.id}`;

                promises.push(
                    fetchWithAuth(url, {
                        method: 'PUT',
                        body: JSON.stringify(formattedPriceItem),
                    }).then(res => {
                        return res;
                    })
                );
            });

            // CREATE new sites using POST
            sitesToCreate.forEach(site => {
                const createUrl = `${API_BASE_URL}/tenant-site-price-list-items`;
                const createData = {
                    tenant_id: currentTenant?.id,
                    site_id: site.site_id,
                    price_list_item_key: editPriceItemKey,
                    ...formattedPriceItem
                };

                promises.push(
                    fetchWithAuth(createUrl, {
                        method: 'POST',
                        body: JSON.stringify(createData),
                    })
                );
            });

            // DELETE removed sites using DELETE
            const deleteJobs = [];
            sitesToDelete.forEach(site => {
                if (site?.tenant_site_id || site?.site_id) {
                    deleteJobs.push({ site, url: buildDeletePriceItemUrl(editPriceItemKey, site) });
                    promises.push(
                        fetchWithAuth(buildDeletePriceItemUrl(editPriceItemKey, site), {
                            method: 'DELETE',
                        })
                    );
                }
            });

            const results = await Promise.all(promises);

            const parsedResults = await Promise.all(
                results.map(async (res, index) => {
                    const parsed = await parseJsonResponse(res);
                    const isDelete = index >= (results.length - deleteJobs.length);
                    const deleteJob = isDelete ? deleteJobs[index - (results.length - deleteJobs.length)] : null;
                    return { ...parsed, isDelete, deleteJob };
                })
            );

            const failures = parsedResults.filter(res => !res.ok);
            if (failures.length > 0) {
                const detachable = failures.filter(isDetachConflict);
                const otherFailures = failures.filter(f => !isDetachConflict(f));

                if (otherFailures.length > 0) {
                    console.error('Some operations failed:', otherFailures);
                    alert(otherFailures.find(e => e.body?.error)?.body?.error || 'Some changes could not be saved (server error). The item has been partially updated — please check and try again.');
                    fetchPriceItems();
                    return;
                }

                const quotationCount = detachable.reduce((sum, e) => sum + (Number(e.body.quotation_count) || 0), 0);
                const activeCount = detachable.reduce((sum, e) => sum + (Number(e.body.active_count) || 0), 0);
                const confirmed = window.confirm(detachConfirmMessage(quotationCount, activeCount));
                if (!confirmed) {
                    fetchPriceItems();
                    return;
                }

                const retryResults = await Promise.all(
                    detachable
                        .filter((failure) => failure.deleteJob?.site)
                        .map((failure) =>
                            deletePriceItemFromSite(editPriceItemKey, failure.deleteJob.site, { confirmDetach: true })
                        )
                );
                const retryFailures = retryResults.filter(res => !res.ok);
                if (retryFailures.length > 0) {
                    alert(retryFailures.find(e => e.body?.error)?.body?.error || 'Some changes could not be saved (server error). The item has been partially updated — please check and try again.');
                    fetchPriceItems();
                    return;
                }
            }

            fetchPriceItems();
            setEditPriceItemId(null);
            setEditPriceItemKey(null);
            
        } catch (error) {
            console.error('Error updating price item:', error);
            alert('Error updating price item. Please try again.');
        }
    };

    // Handle cancel edit
    const handleCancelEdit = () => {
        setEditPriceItemId(null);
        setEditPriceItemKey(null);
        setEditPriceItemSites([]);
        setValidationErrors({});
    };

    const buildDeletePriceItemUrl = (priceItemKey, site, { confirmDetach = false } = {}) => {
        const params = new URLSearchParams();
        if (currentTenant?.id) params.append('tenant_id', currentTenant.id);
        if (site?.tenant_site_id) {
            params.append('tenant_site_id', site.tenant_site_id);
        } else if (site?.site_id) {
            params.append('site_id', site.site_id);
        }
        if (confirmDetach) params.append('confirm_detach', 'true');
        return `${API_BASE_URL}/tenant-site-price-list-items/${encodeURIComponent(priceItemKey)}?${params.toString()}`;
    };

    const parseJsonResponse = async (res) => {
        let body = {};
        try {
            body = await res.json();
        } catch {
            body = {};
        }
        return { ok: res.ok, status: res.status, body };
    };

    const isDetachConflict = (parsed) =>
        parsed?.body?.can_detach === true &&
        (parsed?.body?.code === 'PRICE_ITEM_IN_QUOTATION' || parsed?.status === 409);

    const detachConfirmMessage = (quotationCount, activeCount) => {
        const n = Number(quotationCount) || 0;
        const a = Number(activeCount) || 0;
        return `Used on ${n} quotation${n === 1 ? '' : 's'} (${a} active). Existing quotes keep the current charge.`;
    };

    const deletePriceItemFromSite = async (priceItemKey, site, { confirmDetach = false } = {}) => {
        const url = buildDeletePriceItemUrl(priceItemKey, site, { confirmDetach });
        const res = await fetchWithAuth(url, { method: 'DELETE' });
        return parseJsonResponse(res);
    };

    const confirmAndDeletePriceItemFromSite = async (priceItemKey, site) => {
        const first = await deletePriceItemFromSite(priceItemKey, site);
        if (first.ok || !isDetachConflict(first)) return first;
        const confirmed = window.confirm(
            detachConfirmMessage(first.body.quotation_count, first.body.active_count)
        );
        if (!confirmed) return { ...first, cancelled: true };
        return deletePriceItemFromSite(priceItemKey, site, { confirmDetach: true });
    };

    const handleDeletePriceItem = async (priceItemKey, sites) => {
        if (!priceItemKey) {
            alert('Cannot delete: No price item key');
            return;
        }

        try {
            const sitesToDelete = (sites && sites.length > 0)
                ? sites.filter((site) => site.tenant_site_id || site.site_id)
                : [];

            if (sitesToDelete.length === 0) {
                const params = new URLSearchParams();
                params.append('tenant_id', currentTenant?.id);
                const url = `${API_BASE_URL}/tenant-site-price-list-items/${encodeURIComponent(priceItemKey)}?${params.toString()}`;
                const response = await fetchWithAuth(url, { method: 'DELETE' });
                const parsed = await parseJsonResponse(response);
                if (parsed.ok) {
                    fetchPriceItems();
                    return;
                }
                if (isDetachConflict(parsed)) {
                    const confirmed = window.confirm(
                        detachConfirmMessage(parsed.body.quotation_count, parsed.body.active_count)
                    );
                    if (!confirmed) return;
                    const retry = await fetchWithAuth(
                        `${url}${url.includes('?') ? '&' : '?'}confirm_detach=true`,
                        { method: 'DELETE' }
                    );
                    const retryParsed = await parseJsonResponse(retry);
                    if (retryParsed.ok) {
                        fetchPriceItems();
                        return;
                    }
                    alert(retryParsed.body.error || 'Failed to delete price item. Please try again.');
                    return;
                }
                alert(parsed.body.error || 'Failed to delete price item. Please try again.');
                return;
            }

            for (const site of sitesToDelete) {
                const result = await confirmAndDeletePriceItemFromSite(priceItemKey, site);
                if (result.cancelled) return;
                if (!result.ok) {
                    alert(result.body.error || 'Failed to delete some price items. Please try again.');
                    return;
                }
            }

            fetchPriceItems();
        } catch (error) {
            console.error('Error deleting price item:', error);
            alert('Error deleting price item. Please try again.');
        }
    };

    // Get care type names for multiple values
    const getCareTypeNames = (ids) => {
        if (!ids) return '';
        const idArray = ids.split(", ");
        const careTypeNames = idArray.map(id => {
            const careType = customerCareTypes.find(type => type.id.toString() === id.trim());
            return careType ? careType.name : id;
        });
        return careTypeNames.join(", ");
    };

    const allFilteredKeys = (filteredPriceItems || []).map(i => i.price_list_item_key);

    const handleSelectItem = (key, checked) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (checked) next.add(key);
            else next.delete(key);
            return next;
        });
    };

    const handleSelectAll = () => setSelectedKeys(new Set(allFilteredKeys));
    const handleClearSelection = () => setSelectedKeys(new Set());

    const handleBulkEdit = async (changes) => {
        if (Object.keys(changes).length === 0) {
            setShowBulkEditModal(false);
            return;
        }
        setIsBulkEditing(true);
        const keys = Array.from(selectedKeys);
        setBulkEditProgress({ done: 0, total: keys.length });
        let succeeded = 0;
        let failed = 0;
        const tenantId = currentTenant?.id;

        for (const key of keys) {
            const item = priceItems.find(i => i.price_list_item_key === key);
            if (!item) {
                setBulkEditProgress(prev => ({ ...prev, done: prev.done + 1 }));
                continue;
            }

            const merged = {
                general_category: changes.general_category !== undefined ? changes.general_category : (item.general_category || ''),
                frequency: changes.frequency !== undefined ? changes.frequency : (item.frequency || ''),
                sn_hours_day: changes.sn_hours_day !== undefined ? changes.sn_hours_day : (item.sn_hours_day || '0'),
                sn_hours_night: changes.sn_hours_night !== undefined ? changes.sn_hours_night : (item.sn_hours_night || '0'),
                ena_hours_day: changes.ena_hours_day !== undefined ? changes.ena_hours_day : (item.ena_hours_day || '0'),
                ena_hours_night: changes.ena_hours_night !== undefined ? changes.ena_hours_night : (item.ena_hours_night || '0'),
                cw_hours_day: changes.cw_hours_day !== undefined ? changes.cw_hours_day : (item.cw_hours_day || '0'),
                cw_hours_night: changes.cw_hours_night !== undefined ? changes.cw_hours_night : (item.cw_hours_night || '0'),
                catering: changes.catering !== undefined ? changes.catering : (item.catering || '0'),
                cleaning: changes.cleaning !== undefined ? changes.cleaning : (item.cleaning || '0'),
                laundry: changes.laundry !== undefined ? changes.laundry : (item.laundry || '0'),
                other: changes.other !== undefined ? changes.other : (item.other || '0'),
                customer_care_type_id: changes.customer_care_type_id !== undefined ? changes.customer_care_type_id : (item.customer_care_type_id || null),
                date_from: changes.date_from !== undefined ? changes.date_from : (item.date_from || ''),
                date_to: changes.date_to !== undefined ? changes.date_to : (item.date_to || ''),
            };

            const priceToSave = item.price_overridden ? item.price_manual : calculatePrice(merged);
            const careTypeId = merged.customer_care_type_id ? String(merged.customer_care_type_id).trim() : null;

            const metadata = {
                item: { key, name: item.item_name },
                catalogue: {
                    general_category: merged.general_category,
                    defaults: {
                        frequency: merged.frequency,
                        sn_hours_day: merged.sn_hours_day,
                        sn_hours_night: merged.sn_hours_night,
                        ena_hours_day: merged.ena_hours_day,
                        ena_hours_night: merged.ena_hours_night,
                        cw_hours_day: merged.cw_hours_day,
                        cw_hours_night: merged.cw_hours_night,
                    }
                },
                default_on_quote: careTypeId,
                customer_care_type_id: careTypeId,
                description: item.description || '',
                catering: merged.catering,
                cleaning: merged.cleaning,
                laundry: merged.laundry,
                other: merged.other,
                price: priceToSave,
                price_overridden: item.price_overridden || false,
                price_manual: item.price_manual || '',
                date_from: merged.date_from,
                date_to: merged.date_to,
            };

            const payload = {
                price_list_item_name: item.item_name || '',
                item_name: item.item_name || '',
                price: priceToSave,
                customer_care_type_id: careTypeId,
                ...merged,
                description: item.description || '',
                metadata,
            };

            const itemSites = item.sites || [];

            try {
                let results;
                if (changes.sites !== undefined) {
                    const origSiteIds = new Set(itemSites.map(s => s.site_id));
                    const newSites = changes.sites;
                    const newSiteIds = new Set(newSites.map(s => s.site_id));
                    results = await Promise.all([
                        ...itemSites
                            .filter(s => newSiteIds.has(s.site_id))
                            .map(site => fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-price-list-items/${key}?tenant_id=${tenantId}&site_id=${site.site_id}`,
                                { method: 'PUT', body: JSON.stringify(payload) }
                            )),
                        ...newSites
                            .filter(s => !origSiteIds.has(s.site_id))
                            .map(site => fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-price-list-items`,
                                { method: 'POST', body: JSON.stringify({ tenant_id: tenantId, site_id: site.site_id, price_list_item_key: key, price_list_item_name: item.item_name || '', metadata }) }
                            )),
                        ...itemSites
                            .filter(s => !newSiteIds.has(s.site_id))
                            .map(site => fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-price-list-items/${encodeURIComponent(key)}?tenant_id=${tenantId}&site_id=${site.site_id}`,
                                { method: 'DELETE' }
                            )),
                    ]);
                } else {
                    results = await Promise.all(
                        itemSites.map(site =>
                            fetchWithAuth(
                                `${API_BASE_URL}/tenant-site-price-list-items/${key}?tenant_id=${tenantId}&site_id=${site.site_id}`,
                                { method: 'PUT', body: JSON.stringify(payload) }
                            )
                        )
                    );
                }
                if (results.every(r => r.ok)) succeeded++;
                else failed++;
            } catch {
                failed++;
            }

            setBulkEditProgress(prev => ({ ...prev, done: prev.done + 1 }));
        }

        setIsBulkEditing(false);
        setShowBulkEditModal(false);
        setSelectedKeys(new Set());

        if (failed > 0) {
            const siteNote = changes.sites !== undefined ? ' One or more site assignments may already exist.' : '';
            alert(`Bulk edit complete. ${succeeded} succeeded, ${failed} failed.${siteNote}`);
        }

        fetchPriceItems();
    };

    return (
        <div className="activities-container">
            <div className="page-hero">
                <div className="page-hero-text">
                    <h1 className="page-hero-title">Price Items</h1>
                    <p className="page-hero-subtitle">View and manage all price list items</p>
                </div>
                <div className="page-hero-actions">
                    <div className="page-stat-chip">
                        <div className="page-stat-value">{filteredPriceItems?.length ?? 0}</div>
                        <div className="page-stat-label">Total</div>
                    </div>
                </div>
            </div>

            <div className="page-toolbar">
                <div className="page-toolbar-left">
                    <Form.Control
                        type="text"
                        placeholder="Search by item name..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="search-bar"
                    />
                    <div className="site-filter-dropdown">
                        <Multiselect
                            options={availableSites}
                            displayValue="site_name"
                            selectedValues={selectedSites}
                            onSelect={handleSiteFilterChange}
                            onRemove={handleSiteFilterChange}
                            placeholder="Filter by Sites"
                            {...MULTISELECT_COMMON_PROPS}
                            style={MULTISELECT_THEME_STYLE}
                        />
                    </div>
                </div>
                <div className="page-toolbar-actions">
                    <PermissionGuard
                        permission={PERMISSIONS.PRICE_ITEM_CREATE}
                        showDisabled
                        tooltip="You need Administrator permissions to create Price Items"
                    >
                        <Button
                            className="add-activity-btn"
                            style={{ backgroundColor: 'var(--theme-accent)', borderColor: 'var(--theme-accent)' }}
                            data-tooltip-id="add-price-item-tooltip"
                            onClick={() => {
                                if (hasPermission(PERMISSIONS.PRICE_ITEM_CREATE)) {
                                    handleAddPriceItemClick();
                                }
                            }}
                        >
                            Add New Price Item
                        </Button>
                    </PermissionGuard>
                </div>
            </div>
            {isLoading && <TableSkeleton columns={12} rows={8} message="Loading price items…" />}
            {!isLoading && (
            <div className="table-controls">
                <Button variant="link" className="btn-accent" onClick={() => toggleRowCollapse('details')}>
                    {collapsedRows['details'] ? <FaAngleRight /> : <FaAngleLeft />}
                    {collapsedRows['details'] ? "Expand Columns" : "Collapse Columns"}
                </Button>
            </div>
            )}
            {!isLoading && (
            <div className="table-wrapper" style={{
                overflowX: 'auto',
                overflowY: 'auto',
                maxHeight: '60vh',
                position: 'relative',
                padding: 0,
                borderTop: '1px solid #f5f5dc'
            }}>
                <Table striped bordered hover style={{
                    position: 'relative',
                    margin: 0,
                    borderCollapse: 'collapse'
                }}>
                    <thead style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: 'var(--theme-table-header-bg)',
                        margin: 0,
                        padding: 0
                    }}>
                        <tr>
                            <th rowSpan={2} style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center', width: '40px', verticalAlign: 'middle' }}>
                                <input
                                    type="checkbox"
                                    checked={allFilteredKeys.length > 0 && allFilteredKeys.every(k => selectedKeys.has(k))}
                                    onChange={e => e.target.checked ? handleSelectAll() : handleClearSelection()}
                                    style={{ cursor: 'pointer' }}
                                />
                            </th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}>Actions</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('item_name')}>Item Name{getSortIndicator('item_name')}</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}>Description</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}>Sites</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('price', true)}>Price{getSortIndicator('price')}</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('customer_care_type_id')}>Default on Quote{getSortIndicator('customer_care_type_id')}</th>
                            {canViewBilling && <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}>GL Accounts</th>}
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('general_category')}>General Category{getSortIndicator('general_category')}</th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('frequency')}>Frequency{getSortIndicator('frequency')}</th>
                            {collapsedRows['details'] ? null : (
                                <>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} colSpan={2}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Minutes" /></th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} colSpan={2}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Minutes" /></th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} colSpan={2}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Minutes" /></th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} colSpan={4}>Additional Costs</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('date_from', true)}>From{getSortIndicator('date_from')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('date_to', true)}>To{getSortIndicator('date_to')}</th>
                                </>
                            )}
                        </tr>

                        <tr>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            {canViewBilling && <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>}
                            <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                            {collapsedRows['details'] ? null : (
                                <>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('sn_hours_day', true)}>Day{getSortIndicator('sn_hours_day')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('sn_hours_night', true)}>Night{getSortIndicator('sn_hours_night')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('ena_hours_day')}>Day{getSortIndicator('ena_hours_day')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('ena_hours_night')}>Night{getSortIndicator('ena_hours_night')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('cw_hours_day')}>Day{getSortIndicator('cw_hours_day')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('cw_hours_night')}>Night{getSortIndicator('cw_hours_night')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('catering', true)}>Catering{getSortIndicator('catering')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('cleaning', true)}>Cleaning{getSortIndicator('cleaning')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('laundry', true)}>Laundry{getSortIndicator('laundry')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }} onClick={() => handleSort('other', true)}>Other{getSortIndicator('other')}</th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                                    <th style={{ backgroundColor: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '8px 4px', margin: 0, borderBottom: '2px solid #dee2e6', textAlign: 'center' }}></th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPriceItems?.map(item => (
                            <tr
                                key={item.id || item.price_list_item_id}
                                className={isZeroPrice(item.price) ? 'zero-price-row-flag' : undefined}
                                style={selectedKeys.has(item.price_list_item_key) ? { backgroundColor: 'var(--theme-table-row-selected)' } : {}}
                            >
                                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedKeys.has(item.price_list_item_key)}
                                        onChange={e => handleSelectItem(item.price_list_item_key, e.target.checked)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </td>
                                <td style={{ padding: 0 }}>
                                    <div className="actions-cell">
                                        <PermissionGuard
                                            permission={PERMISSIONS.PRICE_ITEM_UPDATE}
                                            showDisabled
                                            tooltip="You don't have permission to edit Price Items"
                                        >
                                            <Button
                                                variant="link"
                                                data-tooltip-id={`edit-tooltip-${item.id || item.price_list_item_id}`}
                                                style={{
                                                    cursor: hasPermission(PERMISSIONS.PRICE_ITEM_UPDATE) ? 'pointer' : 'not-allowed',
                                                    opacity: hasPermission(PERMISSIONS.PRICE_ITEM_UPDATE) ? 1 : 0.5
                                                }}
                                                onClick={() => {
                                                    if (hasPermission(PERMISSIONS.PRICE_ITEM_UPDATE)) {
                                                        handleEditClick(item);
                                                    }
                                                }}
                                            >
                                                <FaEdit
                                                    style={{ color: hasPermission(PERMISSIONS.PRICE_ITEM_UPDATE) ? '#007bff' : '#cccccc', fontSize: '1.2rem' }}
                                                />
                                            </Button>
                                        </PermissionGuard>
                                        <ReactTooltip
                                            style={{ zIndex: 1000 }}
                                            id={`edit-tooltip-${item.id || item.price_list_item_id}`}
                                            place="top"
                                            content="Edit Price Item"
                                        />

                                        <PermissionGuard
                                            permission={PERMISSIONS.PRICE_ITEM_DELETE}
                                            showDisabled
                                            tooltip="You don't have permission to delete Price Items"
                                        >
                                            <Button
                                                variant="link"
                                                data-tooltip-id={`delete-tooltip-${item.id || item.price_list_item_id}`}
                                                style={{
                                                    cursor: hasPermission(PERMISSIONS.PRICE_ITEM_DELETE) ? 'pointer' : 'not-allowed',
                                                    opacity: hasPermission(PERMISSIONS.PRICE_ITEM_DELETE) ? 1 : 0.5
                                                }}
                                                onClick={() => {
                                                    if (hasPermission(PERMISSIONS.PRICE_ITEM_DELETE)) {
                                                        if (window.confirm("Are you sure you want to remove this item?")) {
                                                            handleDeletePriceItem(item.price_list_item_key, item.sites);
                                                        }
                                                    }
                                                }}
                                            >
                                                <FaTrash
                                                    style={{ color: hasPermission(PERMISSIONS.PRICE_ITEM_DELETE) ? '#dc3545' : '#cccccc', fontSize: '1.2rem' }}
                                                />
                                            </Button>
                                        </PermissionGuard>
                                        <ReactTooltip
                                            style={{ zIndex: 1000 }}
                                            id={`delete-tooltip-${item.id || item.price_list_item_id}`}
                                            place="top"
                                            content="Delete Price Item"
                                        />
                                    </div>
                                </td>
                                <td>{item.item_name}</td>
                                <td>
                                    <DescriptionCell
                                        description={item.description}
                                        id={item.id || item.price_list_item_id || item.price_list_item_key}
                                    />
                                </td>
                                <td><SitesDisplay sites={item.sites} /></td>
                                <td>
                                    <div className="zero-price-cell">
                                        <span>{formatCurrency(item.price)}</span>
                                        {isZeroPrice(item.price) && (
                                            <span
                                                className={`zero-price-chip ${hasLaborMinutes(item) ? 'zero-price-chip--strong' : ''}`}
                                                title={getZeroPriceMessage({ hasLabor: hasLaborMinutes(item) })}
                                                aria-label={getZeroPriceMessage({ hasLabor: hasLaborMinutes(item) })}
                                            >
                                                {hasLaborMinutes(item) ? 'R0 · labour' : 'R0'}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>{item.customer_care_type_id?.split(", ").map(id => {
                                    const careType = customerCareTypes.find(type => type.id.toString() === id.trim());
                                    return careType ? careType.name : id;
                                }).join(", ")}</td>
                                {canViewBilling && (
                                    <td>
                                        {item.gl_allocations && item.gl_allocations.length > 0 ? (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                                {item.gl_allocations.map((alloc, i) => (
                                                    <span key={i} style={{
                                                        backgroundColor: 'var(--theme-table-header-bg)',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        fontFamily: 'monospace',
                                                        color: 'var(--theme-table-header-text)',
                                                    }}>
                                                        {alloc.gl_account_code}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                                        )}
                                    </td>
                                )}
                                <td>{item.general_category}</td>
                                <td>{item.frequency}</td>
                                {collapsedRows['details'] ? null : (
                                    <>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(item.sn_hours_day)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(item.sn_hours_night)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(item.ena_hours_day)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(item.ena_hours_night)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(item.cw_hours_day)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatMinutes(item.cw_hours_night)}</td>
                                        <td>{formatCurrency(item.catering)}</td>
                                        <td>{formatCurrency(item.cleaning)}</td>
                                        <td>{formatCurrency(item.laundry)}</td>
                                        <td>{formatCurrency(item.other)}</td>
                                        <td>{item.date_from ? new Date(item.date_from).toISOString().split('T')[0] : ''}</td>
                                        <td>{item.date_to ? new Date(item.date_to).toISOString().split('T')[0] : ''}</td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            )}

            {/* Tooltips for action buttons */}
            <ReactTooltip
                style={{ zIndex: 1000 }}
                id="add-price-item-tooltip"
                place="top"
                content="Add New Price Item"
            />
            <ReactTooltip
                style={{ zIndex: 1000 }}
                isMulti
                selector="[data-tooltip-id^='edit-tooltip-']"
                place="top"
                content="Edit Price Item"
            />
            <ReactTooltip
                style={{ zIndex: 1000 }}
                isMulti
                selector="[data-tooltip-id^='delete-tooltip-']"
                place="top"
                content="Delete Price Item"
            />

            <Modal size="xl" show={AddPriceItem} onHide={() => {
                setAddPriceItem(false);
                setValidationErrors({});
            }}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Price Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="row">
                            {/* Left Column */}
                            <div className="col-md-6">
                                <Form.Group controlId="formItemName" className="mb-3">
                                    <Form.Label>Price Item Name <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newPriceItem.item_name}
                                        onChange={(e) => {
                                            setNewPriceItem({ ...newPriceItem, item_name: e.target.value });
                                            if (validationErrors.item_name) {
                                                setValidationErrors(prev => ({ ...prev, item_name: null }));
                                            }
                                        }}
                                        isInvalid={!!validationErrors.item_name}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.item_name}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId="formDescription" className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newPriceItem.description}
                                        onChange={(e) => setNewPriceItem({ ...newPriceItem, description: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group controlId="formPrice" className="mb-3">
                                    <Form.Label>Price ({newPriceItem.price_overridden ? 'Modified' : 'Calculated'})</Form.Label>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Control
                                            type="text"
                                            value={newPriceItem.price_overridden ? newPriceItem.price : calculatePrice(newPriceItem)}
                                            onChange={(e) => {
                                                const price = e.target.value;
                                                setNewPriceItem(prev => ({
                                                    ...prev,
                                                    price: price,
                                                    price_overridden: true,
                                                    price_manual: price
                                                }));
                                            }}
                                            style={{ backgroundColor: newPriceItem.price_overridden ? '#fff3cd' : '#f8f9fa', flex: '1' }}
                                        />
                                        {newPriceItem.price_overridden && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => resetPriceToCalculated(newPriceItem, setNewPriceItem)}
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </div>
                                    {newPriceItem.price_overridden && (
                                        <Form.Text className="text-warning">
                                            Price manually overridden
                                        </Form.Text>
                                    )}
                                    {(() => {
                                        const effectivePrice = newPriceItem.price_overridden
                                            ? newPriceItem.price_manual ?? newPriceItem.price
                                            : calculatePrice(newPriceItem);
                                        if (!isZeroPrice(effectivePrice)) return null;
                                        const labor = hasLaborMinutes(newPriceItem);
                                        return (
                                            <div className={`zero-price-warning ${labor ? 'zero-price-warning--strong' : ''}`}>
                                                {getZeroPriceMessage({ hasLabor: labor })}
                                            </div>
                                        );
                                    })()}
                                </Form.Group>

                                <Form.Group controlId="formCustomerCareTypeID" className="mb-3">
                                    <Form.Label>Default on Quote</Form.Label>
                                    <MultiselectWithSelectAll
                                        options={customerCareTypes}
                                        displayValue="name"
                                        selectedValues={
                                            customerCareTypes.filter(item =>
                                                item?.id && newPriceItem.customer_care_type_id?.split(", ").includes(item.id.toString())
                                            )
                                        }
                                        onSelect={(selectedList) => {
                                            const selectedIds = selectedList.map(item => item.id).join(", ");
                                            setNewPriceItem(prev => ({ ...prev, customer_care_type_id: selectedIds }));
                                        }}
                                        onRemove={(selectedList) => {
                                            const selectedIds = selectedList.map(item => item.id).join(", ");
                                            setNewPriceItem(prev => ({ ...prev, customer_care_type_id: selectedIds }));
                                        }}
                                        placeholder="Select service types"
                                    />
                                </Form.Group>

                                <Form.Group controlId="formGeneralCategory" className="mb-3">
                                    <Form.Label>General Category</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={newPriceItem.general_category}
                                        onChange={(e) => setNewPriceItem({ ...newPriceItem, general_category: e.target.value })}
                                    >
                                        <option value="">Select a category</option>
                                        {GENERAL_CATEGORY_OPTIONS.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>

                                <Form.Group controlId="formFrequency" className="mb-3">
                                    <Form.Label>Frequency</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={newPriceItem.frequency}
                                        onChange={(e) => setNewPriceItem({ ...newPriceItem, frequency: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group controlId="formSites" className="mb-3">
                                    <Form.Label>Sites <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <MultiselectWithSelectAll
                                        options={availableSites}
                                        displayValue="site_name"
                                        selectedValues={newPriceItemSites}
                                        onSelect={(selectedList) => {
                                            setNewPriceItemSites(selectedList);
                                            if (validationErrors.sites) {
                                                setValidationErrors(prev => ({ ...prev, sites: null }));
                                            }
                                        }}
                                        onRemove={(selectedList) => setNewPriceItemSites(selectedList)}
                                        placeholder="Select Sites"
                                        style={{
                                            ...MULTISELECT_THEME_STYLE,
                                            ...(validationErrors.sites ? {
                                                searchBox: { ...MULTISELECT_THEME_STYLE.searchBox, border: '1px solid #dc3545' },
                                                optionContainer: { ...MULTISELECT_THEME_STYLE.optionContainer, border: '1px solid #dc3545' }
                                            } : {})
                                        }}
                                    />
                                    {validationErrors.sites && (
                                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                            {validationErrors.sites}
                                        </div>
                                    )}
                                </Form.Group>
                            </div>

                            {/* Right Column */}
                            <div className="col-md-6">
                                {/* Minutes Section */}
                                <div className="mb-3">
                                    <h6 style={SECTION_TITLE_STYLE}>Minutes</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formsn_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('SN')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.sn_hours_day}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, sn_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formsn_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('SN')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.sn_hours_night}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, sn_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formena_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('ENA')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.ena_hours_day}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, ena_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formena_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('ENA')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.ena_hours_night}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, ena_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formcw_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('CW')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.cw_hours_day}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, cw_hours_day: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formcw_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('CW')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.cw_hours_night}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, cw_hours_night: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Costs Section */}
                                <div className="mb-3">
                                    <h6 style={SECTION_TITLE_STYLE}>Additional Costs</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formCatering" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Catering</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.catering}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, catering: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formCleaning" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Cleaning</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.cleaning}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, cleaning: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formLaundry" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Laundry</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.laundry}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, laundry: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formOther" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Other</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={newPriceItem.other}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, other: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Dates Section */}
                                <div className="mb-3">
                                    <h6 style={SECTION_TITLE_STYLE}>Valid Period</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formdate_from" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Date From</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={newPriceItem.date_from}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, date_from: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formdate_to" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Date To</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={newPriceItem.date_to}
                                                    onChange={(e) => setNewPriceItem({ ...newPriceItem, date_to: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* GL Allocations — full-width section */}
                        {canViewBilling && (() => {
                            const itemPrice = parseFloat(newPriceItem.price_overridden ? newPriceItem.price : calculatePrice(newPriceItem)) || 0;
                            const allocated = newPriceItem.gl_allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                            const overBudget = allocated > itemPrice + 0.005;
                            const unallocated = itemPrice - allocated;
                            const lastIdx = newPriceItem.gl_allocations.length - 1;
                            const otherRowsSum = lastIdx > 0
                                ? newPriceItem.gl_allocations.slice(0, lastIdx).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0)
                                : 0;
                            const allocBtnLabel = (newPriceItem.gl_allocations.length > 1 && otherRowsSum > 0.005)
                                ? `Allocate Remaining (R ${Math.max(0, itemPrice - otherRowsSum).toFixed(2)})`
                                : 'Allocate Full Amount';
                            return (
                                <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--theme-border)' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--theme-text)' }}>GL Account Allocations</h6>
                                            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                                                Split this item's price across GL accounts for the billing sheet.
                                            </p>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                disabled={newPriceItem.gl_allocations.length === 0}
                                                onClick={() => {
                                                    const allocs = [...newPriceItem.gl_allocations];
                                                    allocs[lastIdx] = { ...allocs[lastIdx], amount: Math.max(0, itemPrice - otherRowsSum).toFixed(2) };
                                                    setNewPriceItem(prev => ({ ...prev, gl_allocations: allocs }));
                                                }}
                                            >
                                                {allocBtnLabel}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setNewPriceItem(prev => ({
                                                    ...prev,
                                                    gl_allocations: [...prev.gl_allocations, { gl_account_code: '', amount: '' }]
                                                }))}
                                            >
                                                + Add GL Account
                                            </Button>
                                        </div>
                                    </div>
                                    {newPriceItem.gl_allocations.length === 0 ? (
                                        <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                                            No GL accounts allocated — the full item price will appear as unallocated on the billing sheet.
                                        </p>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 36px', gap: '6px 8px', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600 }}>GL Account</span>
                                            <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textAlign: 'right' }}>Amount (R)</span>
                                            <span />
                                            {newPriceItem.gl_allocations.map((alloc, idx) => (
                                                <>
                                                    <Form.Select
                                                        key={`sel-${idx}`}
                                                        value={alloc.gl_account_code}
                                                        isInvalid={!!validationErrors.gl_allocations && !alloc.gl_account_code}
                                                        onChange={e => {
                                                            const updated = [...newPriceItem.gl_allocations];
                                                            updated[idx] = { ...updated[idx], gl_account_code: e.target.value };
                                                            setNewPriceItem(prev => ({ ...prev, gl_allocations: updated }));
                                                            if (validationErrors.gl_allocations) {
                                                                setValidationErrors(prev => ({ ...prev, gl_allocations: null }));
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Select GL account…</option>
                                                        {(() => {
                                                            const usedCodes = new Set(
                                                                newPriceItem.gl_allocations
                                                                    .filter((_, i) => i !== idx)
                                                                    .map(a => a.gl_account_code)
                                                                    .filter(Boolean)
                                                            );
                                                            return billingGroupsList.map(bg => {
                                                                const bgAccounts = glAccounts.filter(a => a.billing_group_id === bg.id && !usedCodes.has(a.code));
                                                                if (bgAccounts.length === 0) return null;
                                                                return (
                                                                    <optgroup key={bg.id} label={bg.name}>
                                                                        {bgAccounts.map(a => (
                                                                            <option key={a.id} value={a.code}>{a.code} — {a.name}</option>
                                                                        ))}
                                                                    </optgroup>
                                                                );
                                                            });
                                                        })()}
                                                    </Form.Select>
                                                    <Form.Control
                                                        key={`amt-${idx}`}
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={alloc.amount}
                                                        min={0}
                                                        step="0.01"
                                                        onChange={e => {
                                                            const updated = [...newPriceItem.gl_allocations];
                                                            updated[idx] = { ...updated[idx], amount: e.target.value };
                                                            setNewPriceItem(prev => ({ ...prev, gl_allocations: updated }));
                                                        }}
                                                        style={{ textAlign: 'right' }}
                                                    />
                                                    <Button
                                                        key={`rm-${idx}`}
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => {
                                                            const updated = newPriceItem.gl_allocations.filter((_, i) => i !== idx);
                                                            setNewPriceItem(prev => ({ ...prev, gl_allocations: updated }));
                                                        }}
                                                        style={{ padding: '6px 10px' }}
                                                    >
                                                        ×
                                                    </Button>
                                                </>
                                            ))}
                                        </div>
                                    )}
                                    {validationErrors.gl_allocations && (
                                        <p style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: '6px', marginBottom: 0 }}>
                                            Please select a GL account for every row before saving.
                                        </p>
                                    )}
                                    {/* Summary / validation */}
                                    {newPriceItem.gl_allocations.length > 0 && itemPrice > 0 && (
                                        <div style={{
                                            marginTop: '10px',
                                            padding: '10px 14px',
                                            borderRadius: '6px',
                                            backgroundColor: overBudget ? '#fef2f2' : unallocated > 0.005 ? '#fffbeb' : '#f0fdf4',
                                            border: `1px solid ${overBudget ? '#fca5a5' : unallocated > 0.005 ? '#fcd34d' : '#86efac'}`,
                                            fontSize: '0.85rem',
                                        }}>
                                            {overBudget ? (
                                                <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                                    ⚠ Allocations (R {allocated.toFixed(2)}) exceed the item price (R {itemPrice.toFixed(2)}) by R {(allocated - itemPrice).toFixed(2)}. Please reduce allocations before saving.
                                                </span>
                                            ) : (
                                                <span style={{ color: unallocated > 0.005 ? '#92400e' : '#15803d' }}>
                                                    Allocated: R {allocated.toFixed(2)} of R {itemPrice.toFixed(2)}
                                                    {unallocated > 0.005 && <> · <strong>Unallocated: R {unallocated.toFixed(2)}</strong> (will show separately on billing sheet)</>}
                                                    {!overBudget && unallocated <= 0.005 && <> · Fully allocated ✓</>}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {glAccounts.length === 0 && (
                                        <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginTop: '8px' }}>
                                            No GL accounts defined yet. <a href="/gl-accounts">Set up GL accounts</a> to allocate price items.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}

                        <div className="text-center mt-4">
                            <Button className="btn-primary" style={{ backgroundColor: 'var(--theme-accent)', borderColor: 'var(--theme-accent)' }} onClick={handleAddPriceItem}>Add Price Item</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal size="xl" show={editPriceItemId !== null} onHide={() => handleCancelEdit()}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Price Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="row">
                            {/* Left Column */}
                            <div className="col-md-6">
                                <Form.Group controlId="formEditItemName" className="mb-3">
                                    <Form.Label>Price Item Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.item_name}
                                        onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group controlId="formEditDescription" className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group controlId="formEditPrice" className="mb-3">
                                    <Form.Label>Price ({formData.price_overridden ? 'Modified' : 'Calculated'})</Form.Label>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Control
                                            type="text"
                                            value={formData.price}
                                            onChange={(e) => {
                                                const price = e.target.value;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    price: price,
                                                    price_overridden: true,
                                                    price_manual: price
                                                }));
                                            }}
                                            style={{ backgroundColor: formData.price_overridden ? '#fff3cd' : '#f8f9fa', flex: '1' }}
                                        />
                                        {formData.price_overridden && (
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => resetPriceToCalculated(formData, setFormData)}
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </div>
                                    {formData.price_overridden && (
                                        <Form.Text className="text-warning">
                                            Price manually overridden
                                        </Form.Text>
                                    )}
                                    {(() => {
                                        const effectivePrice = formData.price_overridden
                                            ? formData.price_manual ?? formData.price
                                            : formData.price;
                                        if (!isZeroPrice(effectivePrice)) return null;
                                        const labor = hasLaborMinutes(formData);
                                        return (
                                            <div className={`zero-price-warning ${labor ? 'zero-price-warning--strong' : ''}`}>
                                                {getZeroPriceMessage({ hasLabor: labor })}
                                            </div>
                                        );
                                    })()}
                                </Form.Group>

                                <Form.Group controlId="formEditCustomerCareTypeID" className="mb-3">
                                    <Form.Label>Default on Quote</Form.Label>
                                    <MultiselectWithSelectAll
                                        options={customerCareTypes}
                                        displayValue="name"
                                        selectedValues={
                                            customerCareTypes.filter(item =>
                                                item?.id && formData.customer_care_type_id?.split(", ").includes(item.id.toString())
                                            )
                                        }
                                        onSelect={(selectedList) => {
                                            const selectedIds = selectedList.map(item => item.id).join(", ");
                                            setFormData(prev => ({ ...prev, customer_care_type_id: selectedIds }));
                                        }}
                                        onRemove={(selectedList) => {
                                            const selectedIds = selectedList.map(item => item.id).join(", ");
                                            setFormData(prev => ({ ...prev, customer_care_type_id: selectedIds }));
                                        }}
                                        placeholder="Select service types"
                                    />
                                </Form.Group>

                                <Form.Group controlId="formEditGeneralCategory" className="mb-3">
                                    <Form.Label>General Category</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={formData.general_category}
                                        onChange={(e) => setFormData({ ...formData, general_category: e.target.value })}
                                    >
                                        <option value="">Select a category</option>
                                        {GENERAL_CATEGORY_OPTIONS.map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>

                                <Form.Group controlId="formEditFrequency" className="mb-3">
                                    <Form.Label>Frequency</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.frequency}
                                        onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                                    />
                                </Form.Group>

                                <Form.Group controlId="formEditSites" className="mb-3">
                                    <Form.Label>Sites <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <MultiselectWithSelectAll
                                        options={availableSites}
                                        displayValue="site_name"
                                        selectedValues={editPriceItemSites}
                                        onSelect={(selectedList) => setEditPriceItemSites(selectedList)}
                                        onRemove={(selectedList) => setEditPriceItemSites(selectedList)}
                                        placeholder="Select Sites"
                                    />
                                </Form.Group>
                            </div>

                            {/* Right Column */}
                            <div className="col-md-6">
                                {/* Minutes Section */}
                                <div className="mb-3">
                                    <h6 style={SECTION_TITLE_STYLE}>Minutes</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditsn_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('SN')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.sn_hours_day}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, sn_hours_day: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditsn_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('SN')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.sn_hours_night}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, sn_hours_night: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditena_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('ENA')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.ena_hours_day}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, ena_hours_day: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditena_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('ENA')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.ena_hours_night}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, ena_hours_night: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditcw_hours_day" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('CW')} Day</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.cw_hours_day}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, cw_hours_day: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditcw_hours_night" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>{getAbbreviation('CW')} Night</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.cw_hours_night}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, cw_hours_night: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Costs Section */}
                                <div className="mb-3">
                                    <h6 style={SECTION_TITLE_STYLE}>Additional Costs</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditCatering" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Catering</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.catering}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, catering: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditCleaning" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Cleaning</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.cleaning}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, cleaning: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditLaundry" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Laundry</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.laundry}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, laundry: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditOther" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Other</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={formData.other}
                                                    onChange={(e) => {
                                                        const updated = { ...formData, other: e.target.value };
                                                        if (!updated.price_overridden) updated.price = calculatePrice(updated);
                                                        setFormData(updated);
                                                    }}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                {/* Dates Section */}
                                <div className="mb-3">
                                    <h6 style={SECTION_TITLE_STYLE}>Valid Period</h6>
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group controlId="formEditdate_from" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Date From</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={formData.date_from}
                                                    onChange={(e) => setFormData({ ...formData, date_from: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group controlId="formEditdate_to" className="mb-2">
                                                <Form.Label style={{ fontSize: '0.875rem' }}>Date To</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={formData.date_to}
                                                    onChange={(e) => setFormData({ ...formData, date_to: e.target.value })}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* GL Allocations — full-width section */}
                        {canViewBilling && (() => {
                            const itemPrice = parseFloat(formData.price) || 0;
                            const allocated = formData.gl_allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                            const overBudget = allocated > itemPrice + 0.005;
                            const unallocated = itemPrice - allocated;
                            const lastIdx = formData.gl_allocations.length - 1;
                            const otherRowsSum = lastIdx > 0
                                ? formData.gl_allocations.slice(0, lastIdx).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0)
                                : 0;
                            const allocBtnLabel = (formData.gl_allocations.length > 1 && otherRowsSum > 0.005)
                                ? `Allocate Remaining (R ${Math.max(0, itemPrice - otherRowsSum).toFixed(2)})`
                                : 'Allocate Full Amount';
                            return (
                                <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--theme-border)' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h6 style={{ margin: 0, fontWeight: 700, color: 'var(--theme-text)' }}>GL Account Allocations</h6>
                                            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                                                Split this item's price across GL accounts for the billing sheet.
                                            </p>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                disabled={formData.gl_allocations.length === 0}
                                                onClick={() => {
                                                    const allocs = [...formData.gl_allocations];
                                                    allocs[lastIdx] = { ...allocs[lastIdx], amount: Math.max(0, itemPrice - otherRowsSum).toFixed(2) };
                                                    setFormData(prev => ({ ...prev, gl_allocations: allocs }));
                                                }}
                                            >
                                                {allocBtnLabel}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    gl_allocations: [...prev.gl_allocations, { gl_account_code: '', amount: '' }]
                                                }))}
                                            >
                                                + Add GL Account
                                            </Button>
                                        </div>
                                    </div>
                                    {formData.gl_allocations.length === 0 ? (
                                        <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                                            No GL accounts allocated — the full item price will appear as unallocated on the billing sheet.
                                        </p>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 36px', gap: '6px 8px', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600 }}>GL Account</span>
                                            <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textAlign: 'right' }}>Amount (R)</span>
                                            <span />
                                            {formData.gl_allocations.map((alloc, idx) => (
                                                <>
                                                    <Form.Select
                                                        key={`sel-${idx}`}
                                                        value={alloc.gl_account_code}
                                                        isInvalid={!!validationErrors.gl_allocations && !alloc.gl_account_code}
                                                        onChange={e => {
                                                            const updated = [...formData.gl_allocations];
                                                            updated[idx] = { ...updated[idx], gl_account_code: e.target.value };
                                                            setFormData(prev => ({ ...prev, gl_allocations: updated }));
                                                            if (validationErrors.gl_allocations) {
                                                                setValidationErrors(prev => ({ ...prev, gl_allocations: null }));
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Select GL account…</option>
                                                        {(() => {
                                                            const usedCodes = new Set(
                                                                formData.gl_allocations
                                                                    .filter((_, i) => i !== idx)
                                                                    .map(a => a.gl_account_code)
                                                                    .filter(Boolean)
                                                            );
                                                            return billingGroupsList.map(bg => {
                                                                const bgAccounts = glAccounts.filter(a => a.billing_group_id === bg.id && !usedCodes.has(a.code));
                                                                if (bgAccounts.length === 0) return null;
                                                                return (
                                                                    <optgroup key={bg.id} label={bg.name}>
                                                                        {bgAccounts.map(a => (
                                                                            <option key={a.id} value={a.code}>{a.code} — {a.name}</option>
                                                                        ))}
                                                                    </optgroup>
                                                                );
                                                            });
                                                        })()}
                                                    </Form.Select>
                                                    <Form.Control
                                                        key={`amt-${idx}`}
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={alloc.amount}
                                                        min={0}
                                                        step="0.01"
                                                        onChange={e => {
                                                            const updated = [...formData.gl_allocations];
                                                            updated[idx] = { ...updated[idx], amount: e.target.value };
                                                            setFormData(prev => ({ ...prev, gl_allocations: updated }));
                                                        }}
                                                        style={{ textAlign: 'right' }}
                                                    />
                                                    <Button
                                                        key={`rm-${idx}`}
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => {
                                                            const updated = formData.gl_allocations.filter((_, i) => i !== idx);
                                                            setFormData(prev => ({ ...prev, gl_allocations: updated }));
                                                        }}
                                                        style={{ padding: '6px 10px' }}
                                                    >
                                                        ×
                                                    </Button>
                                                </>
                                            ))}
                                        </div>
                                    )}
                                    {validationErrors.gl_allocations && (
                                        <p style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: '6px', marginBottom: 0 }}>
                                            Please select a GL account for every row before saving.
                                        </p>
                                    )}
                                    {/* Summary / validation */}
                                    {formData.gl_allocations.length > 0 && itemPrice > 0 && (
                                        <div style={{
                                            marginTop: '10px',
                                            padding: '10px 14px',
                                            borderRadius: '6px',
                                            backgroundColor: overBudget ? '#fef2f2' : unallocated > 0.005 ? '#fffbeb' : '#f0fdf4',
                                            border: `1px solid ${overBudget ? '#fca5a5' : unallocated > 0.005 ? '#fcd34d' : '#86efac'}`,
                                            fontSize: '0.85rem',
                                        }}>
                                            {overBudget ? (
                                                <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                                    ⚠ Allocations (R {allocated.toFixed(2)}) exceed the item price (R {itemPrice.toFixed(2)}) by R {(allocated - itemPrice).toFixed(2)}. Please reduce allocations before saving.
                                                </span>
                                            ) : (
                                                <span style={{ color: unallocated > 0.005 ? '#92400e' : '#15803d' }}>
                                                    Allocated: R {allocated.toFixed(2)} of R {itemPrice.toFixed(2)}
                                                    {unallocated > 0.005 && <> · <strong>Unallocated: R {unallocated.toFixed(2)}</strong> (will show separately on billing sheet)</>}
                                                    {!overBudget && unallocated <= 0.005 && <> · Fully allocated ✓</>}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {glAccounts.length === 0 && (
                                        <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginTop: '8px' }}>
                                            No GL accounts defined yet. <a href="/gl-accounts">Set up GL accounts</a> to allocate price items.
                                        </p>
                                    )}
                                </div>
                            );
                        })()}

                        <div className="text-center mt-4">
                            <Button variant="primary" onClick={handleSaveClick}>Save Changes</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <BulkEditPriceItemsModal
                show={showBulkEditModal}
                onHide={() => setShowBulkEditModal(false)}
                selectedCount={selectedKeys.size}
                customerCareTypes={customerCareTypes}
                availableSites={availableSites}
                onApply={handleBulkEdit}
                isApplying={isBulkEditing}
                progress={bulkEditProgress}
            />

            {selectedKeys.size > 0 && (
                <div style={{
                    position: 'fixed',
                    bottom: '24px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1f2937',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                    zIndex: 1050,
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                }}>
                    <span style={{ fontWeight: 600 }}>{selectedKeys.size} selected</span>
                    <span style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', alignSelf: 'stretch' }} />
                    <Button
                        variant="link"
                        style={{ color: 'rgba(255,255,255,0.8)', padding: 0, fontSize: '13px', textDecoration: 'underline' }}
                        onClick={handleSelectAll}
                    >
                        Select all ({allFilteredKeys.length})
                    </Button>
                    <span style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', alignSelf: 'stretch' }} />
                    <PermissionGuard
                        permission={PERMISSIONS.PRICE_ITEM_UPDATE}
                        showDisabled
                        tooltip="You need update permissions to bulk edit Price Items"
                    >
                        <Button
                            variant="light"
                            size="sm"
                            onClick={() => setShowBulkEditModal(true)}
                        >
                            Edit fields
                        </Button>
                    </PermissionGuard>
                    <Button
                        variant="link"
                        style={{ color: 'rgba(255,255,255,0.6)', padding: 0, fontSize: '16px', lineHeight: 1 }}
                        onClick={handleClearSelection}
                    >
                        <FaTimesCircle />
                    </Button>
                </div>
            )}

        </div>
    );
}

export default PriceItems;
